/**
 * Type, helpers, and hook shared between the two invite-a-spark surfaces
 * (InviteSparkFlow on the event page, SparkActionsDrawer on /matches).
 * Components live in InviteSparkPrimitives.tsx so this file's exports can
 * be tree-shaken / fast-refresh cleanly.
 */

import {
  MatchesPageSpark,
  SparkWithRegistrationSummary,
  UpcomingSparkForUser,
} from "@greenflagdate/shared";
import { useMemo } from "react";
import {
  isAgeWithinSparkWindow,
  isSparkFull,
  isSparkFullFor,
} from "../../lib/sparkHelpers";

export type EligibleEvent = {
  sparkId: number;
  title: string | null;
  activity: string;
  startsAt: string;
  viewerRegistered?: boolean;
  image?: string;
  /**
   * Why this event can't be picked, if it can't — the event is full for the
   * inviter's or invitee's gender, or one of them is outside the age window.
   * Undefined means the event is selectable. Disabled rows are shown (not
   * hidden) with this note so the user understands the constraint.
   */
  disabledReason?: string;
};

/** The inviter's own details, needed to check their eligibility per event. */
export type ViewerForInvite = {
  gender?: string;
  age?: number;
};

/**
 * Whether a person of the given gender/age can still take a spot at this event.
 * The rules (age window with 0 = "no limit", total capacity, per-gender caps)
 * live in the shared sparkHelpers — the same implementation the registration
 * flow uses — so the picker and the API gates can't drift. This wrapper only
 * maps a blocked check to a human-readable reason string (used as the row
 * note); null means eligible.
 */
function disabledReasonFor(
  event: SparkWithRegistrationSummary,
  who: "you" | string,
  gender: string | undefined,
  age: number | undefined,
): string | null {
  if (age !== undefined && !isAgeWithinSparkWindow(age, event)) {
    return who === "you"
      ? "You don't meet the age criteria"
      : `${who} doesn't meet the age criteria`;
  }
  if (isSparkFull(event).includes("everyone")) {
    return "This event is full";
  }
  if (isSparkFullFor(gender, event)) {
    return gender?.toLowerCase() === "man" ? "Full for men" : "Full for women";
  }
  return null;
}

/**
 * The reason the INVITEE can't take a spot at this specific event (age window
 * or gender capacity), or null if they're eligible. Used to gate the fixed-
 * event invite surface (SpotSavedWidget → InviteSparkFlow), where the picker's
 * per-row eligibility isn't computed. Mirrors the same rules useEligibleEvents
 * applies to the invitee side.
 */
export function inviteeDisabledReason(
  event: SparkWithRegistrationSummary,
  spark: MatchesPageSpark,
): string | null {
  // Already invited them here and they declined / canceled — re-inviting would
  // be rejected, so surface that instead of a spot-availability reason.
  if (spark.unavailableEventSparkIds?.includes(event.id)) {
    return "They can't make it this time";
  }
  const inviteeName = spark.profile.firstName || "They";
  return disabledReasonFor(event, inviteeName, spark.gender, spark.profile.age);
}

/**
 * Maps a POST /user/spark-invitations error code to an actionable message.
 * Permanent failures (already invited / declined / attending, ineligible) get
 * a specific explanation so the viewer doesn't retry a send that can never
 * succeed; truly transient/unknown failures fall back to "try again".
 */
export function inviteErrorMessage(
  code: string | undefined,
  firstName: string,
): string {
  switch (code) {
    case "ALREADY_INVITED_TO_EVENT":
      return `You've already invited ${firstName} to this event.`;
    case "ALREADY_INVITED":
      return `You already have a pending invite out to ${firstName}. Wait for a response before inviting them again.`;
    case "ALREADY_REGISTERED":
      return `${firstName} is already registered for this event.`;
    case "AGE_INELIGIBLE":
      return `${firstName} doesn't meet this event's age criteria.`;
    case "NO_SPACE_MEN":
    case "NO_SPACE_WOMEN":
      return `This event is full for ${firstName}.`;
    case "EXCEEDS_CAPACITY":
      return "This event is full.";
    case "EVENT_NOT_UPCOMING":
      return "This event has already started.";
    case "NOT_MUTUAL_SPARK":
      return `You can only invite your mutual Sparks to events.`;
    case "NOT_REGISTERED":
      return `Grab your own spot for this event first, then invite ${firstName}.`;
    default:
      return "Couldn't send invite. Please try again.";
  }
}

/**
 * Of the viewer's mutual pre-event sparks, the ones they could actually invite
 * to ONE specific event. Used by the surfaces that decide whether to offer the
 * invite at all (the post-registration prompt only appears when this is
 * non-empty) rather than showing a list where every row is unusable.
 *
 * Someone drops out when:
 *   - the viewer already has an active invite out to them (one at a time), or
 *   - they're already going to this event — nothing to invite them to, or
 *   - the event can't take them: outside its age window, full for their
 *     gender, full outright, or they already said no to this event
 *     (`inviteeDisabledReason` owns all four).
 *
 * `event` is any spark row carrying capacity + age fields, so both the store's
 * `UpcomingSparkForUser` and the event page's freshly fetched
 * `SparkWithRegistrationSummary` work.
 */
export function invitableSparksForEvent(
  sparks: MatchesPageSpark[],
  event: SparkWithRegistrationSummary,
): MatchesPageSpark[] {
  return sparks.filter(
    (spark) =>
      !spark.sentInvitation &&
      spark.upcomingEvent?.sparkId !== event.id &&
      !inviteeDisabledReason(event, spark),
  );
}

/**
 * Eligible events = all of the viewer's known upcoming events (already loaded
 * by /user/state into the store). Events are NOT filtered out — every one is
 * returned, but those the inviter or invitee can't actually take a spot at
 * (full for their gender, or outside the age window) carry a `disabledReason`
 * so the row can be shown greyed-out with a note. Sorted with the spark's own
 * upcoming event first (if any), then chronologically.
 */
export function useEligibleEvents(
  spark: MatchesPageSpark | null | undefined,
  upcoming: UpcomingSparkForUser[] | undefined,
  viewer: ViewerForInvite | null | undefined,
): EligibleEvent[] | null {
  const sparkEventSparkId = spark?.upcomingEvent?.sparkId;
  const inviteeName = spark?.profile.firstName || "They";
  return useMemo(() => {
    if (!spark) return null;
    if (!upcoming) return null;
    const inviteeAge = spark.profile.age;
    const inviteeGender = spark.gender;
    // Events the invitee already declined / canceled — re-inviting is rejected,
    // so grey them out with a "can't make it" note rather than a spot reason.
    const unavailable = new Set(spark.unavailableEventSparkIds ?? []);
    const events = upcoming.map<EligibleEvent>((e) => {
      // Inviter's own eligibility first (they must be able to attend to host
      // the invite), then the invitee's. Skip the inviter's capacity check
      // when they're already registered — they hold a spot, so a gender cap
      // being reached doesn't block them.
      const inviterReason = e.registered
        ? null
        : disabledReasonFor(e, "you", viewer?.gender, viewer?.age);
      // The spark's OWN event is a "join them" target, not an invite target:
      // they already hold a spot there, so their age/capacity eligibility is
      // irrelevant — and an event that's gender-full *because they're in it*
      // must not disable the viewer's option to join. Only the viewer's own
      // eligibility to grab a spot gates that row.
      const inviteeReason =
        e.id === sparkEventSparkId
          ? null
          : unavailable.has(e.id)
            ? "They can't make it this time"
            : disabledReasonFor(e, inviteeName, inviteeGender, inviteeAge);
      const disabledReason = inviterReason ?? inviteeReason ?? undefined;
      return {
        sparkId: e.id,
        title: e.title,
        activity: e.activity,
        startsAt: e.starts_at,
        viewerRegistered: e.registered,
        image: e.primary_image || e.hero_image,
        disabledReason,
      };
    });
    return events.sort((a, b) => {
      if (a.sparkId === sparkEventSparkId) return -1;
      if (b.sparkId === sparkEventSparkId) return 1;
      return new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime();
    });
  }, [spark, upcoming, viewer, sparkEventSparkId, inviteeName]);
}
